import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { ArrowLeftIcon, Factory } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { lottiProduzioneApi } from '@/api/magazzino'
import { tipologieBoxApi } from '@/api/box'
import { useAuth } from '@/hooks/useAuth'
import { fetchAllPaginatedResults } from '@/utils/pagination'

/**
 * Form per creare lotto produzione.
 * Esegue assemblaggio automatico: scarica componenti/prodotti, carica box assemblate.
 */
export default function LottoProduzioneForm() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const tipologiaFromQuery = searchParams.get('tipologia_box') || ''
  const ordineNumeroFromQuery = searchParams.get('ordine_numero') || ''
  const returnToFromQuery = searchParams.get('return_to') || ''
  const quantitaFromQueryRaw = searchParams.get('quantita') || ''
  const quantitaFromQuery = Number.parseInt(quantitaFromQueryRaw, 10)
  const quantitaPrefill = Number.isFinite(quantitaFromQuery) && quantitaFromQuery > 0
    ? String(quantitaFromQuery)
    : '10'

  const [formData, setFormData] = useState({
    codice: '',
    tipologia_box: tipologiaFromQuery,
    riferimento_ordine: ordineNumeroFromQuery,
    quantita: quantitaPrefill,
    note: '',
  })

  // Query tipologie box
  const { data: boxData } = useQuery({
    queryKey: ['tipologie-box-attive', 'all-pages'],
    queryFn: () => fetchAllPaginatedResults(
      (params) => tipologieBoxApi.getAll(params),
      { attivo: true }
    ),
  })

  const tipologiaPreselezionataId = Number.parseInt(tipologiaFromQuery, 10)
  const { data: tipologiaPreselezionataData } = useQuery({
    queryKey: ['tipologie-box', tipologiaPreselezionataId, 'dettaglio-assemblaggio'],
    queryFn: () => tipologieBoxApi.getById(tipologiaPreselezionataId),
    enabled: Number.isInteger(tipologiaPreselezionataId) && tipologiaPreselezionataId > 0,
  })

  // Mutation create
  const createMutation = useMutation({
    mutationFn: lottiProduzioneApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lotti-produzione'] })
      queryClient.invalidateQueries({ queryKey: ['lotti-recenti'] })
      queryClient.invalidateQueries({ queryKey: ['giacenze-prodotti'] })
      queryClient.invalidateQueries({ queryKey: ['giacenze-componenti'] })
      queryClient.invalidateQueries({ queryKey: ['giacenze-box'] })
      queryClient.invalidateQueries({ queryKey: ['movimenti-magazzino'] })
      queryClient.invalidateQueries({ queryKey: ['movimenti-recenti'] })
      toast.success('Lotto creato e assemblaggio completato!')
      if (returnToFromQuery && returnToFromQuery.startsWith('/')) {
        navigate(returnToFromQuery)
      } else {
        navigate('/box/tipologie')
      }
    },
    onError: (error) => {
      console.error('Errore creazione lotto:', error.response?.data)
      
      // Mostra errori di validazione specifici
      if (error.response?.data) {
        const errors = error.response.data
        
        // Gestione errore giacenze insufficienti
        if (errors.giacenze_insufficienti) {
          const messaggi = Array.isArray(errors.giacenze_insufficienti) 
            ? errors.giacenze_insufficienti 
            : [errors.giacenze_insufficienti]
          
          toast.error(
            <div>
              <p className="font-semibold mb-2">{messaggi[0]}</p>
              <ul className="list-disc list-inside space-y-1">
                {messaggi.slice(1).map((msg, i) => (
                  <li key={i} className="text-sm">{msg}</li>
                ))}
              </ul>
            </div>,
            { duration: 8000 }
          )
          return
        }
        
        // Altri errori di validazione
        if (typeof errors === 'object' && !errors.detail) {
          const errorMessages = Object.entries(errors)
            .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
            .join('\n')
          toast.error(errorMessages)
        } else {
          const errorMsg = errors.detail || errors.error || 'Errore durante la creazione'
          toast.error(errorMsg)
        }
      } else {
        toast.error('Errore durante la creazione del lotto')
      }
    },
  })

  const tipologieAttive = Array.isArray(boxData) ? boxData : []
  const tipologiaPreselezionata = tipologiaPreselezionataData?.data
  const tipologie = tipologiaPreselezionata
    ? [
        tipologiaPreselezionata,
        ...tipologieAttive.filter((t) => t.id !== tipologiaPreselezionata.id),
      ]
    : tipologieAttive

  useEffect(() => {
    if (!formData.tipologia_box || tipologie.length === 0) return
    const exists = tipologie.some((t) => t.id === Number.parseInt(formData.tipologia_box, 10))
    if (!exists) {
      toast.error('La tipologia box pre-selezionata non è disponibile')
      setFormData((prev) => ({ ...prev, tipologia_box: '' }))
    }
  }, [tipologie, formData.tipologia_box])

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!formData.tipologia_box) {
      toast.error('Seleziona una tipologia box')
      return
    }

    if (!formData.quantita || parseInt(formData.quantita) <= 0) {
      toast.error('Inserisci una quantità valida')
      return
    }

    if (!user?.id) {
      toast.error('Utente non autenticato')
      return
    }

    // Calcola costi
    const tipologia = tipologie.find(t => t.id === parseInt(formData.tipologia_box))
    const quantita = parseInt(formData.quantita)
    
    // Usa costi dalla tipologia o 0 se non disponibili
    const costoScatola = tipologia?.costo_scatola_cents || 0
    const costoProdotti = tipologia?.costo_prodotti_cents || 0
    const costoTotale = (costoScatola + costoProdotti) * quantita

    const data = {
      tipologia_box: parseInt(formData.tipologia_box),
      quantita: quantita,
      operatore: user.id,
      riferimento_ordine: formData.riferimento_ordine?.trim() || '',
      costo_componenti_cents: costoScatola * quantita,
      costo_prodotti_cents: costoProdotti * quantita,
      costo_totale_cents: costoTotale,
      note: formData.note.trim() || '',
    }

    const codiceManuale = formData.codice.trim()
    if (codiceManuale) {
      data.codice = codiceManuale
    }

    console.log('Dati lotto da inviare:', data)
    createMutation.mutate(data)
  }

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeftIcon className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Nuovo Lotto Produzione</h1>
          <p className="text-muted-foreground">
            Crea un lotto di assemblaggio box (scarica componenti/prodotti, carica box assemblate)
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Factory className="h-5 w-5" />
              Dati Lotto
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Codice Lotto */}
            <div className="space-y-2">
              <Label htmlFor="codice">Codice Lotto (automatico)</Label>
              <Input
                id="codice"
                value={formData.codice}
                disabled
                placeholder={formData.codice ? '' : 'Verrà generato automaticamente (LOTTO-YYYY-NNN)'}
              />
            </div>

            {/* Tipologia Box */}
            <div className="space-y-2">
              <Label htmlFor="tipologia">Tipologia Box *</Label>
              <Select 
                value={formData.tipologia_box} 
                onValueChange={(value) => handleChange('tipologia_box', value)}
              >
                <SelectTrigger id="tipologia">
                  <SelectValue placeholder="Seleziona tipologia box..." />
                </SelectTrigger>
                <SelectContent>
                  {tipologie.map((tipologia) => (
                    <SelectItem key={tipologia.id} value={tipologia.id.toString()}>
                      {tipologia.codice} - {tipologia.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {tipologiaFromQuery && (
                <p className="text-sm text-muted-foreground">
                  Tipologia pre-selezionata dalla lista box. Puoi cambiarla se necessario.
                </p>
              )}
            </div>

            {/* Quantità */}
            <div className="space-y-2">
              <Label htmlFor="quantita">Quantità da Assemblare *</Label>
              <Input
                id="quantita"
                type="number"
                min="1"
                value={formData.quantita}
                onChange={(e) => handleChange('quantita', e.target.value)}
              />
              <p className="text-sm text-muted-foreground">
                ⚠️ Verifica di avere giacenza sufficiente di componenti e prodotti
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="riferimento_ordine">Riferimento Ordine (opzionale)</Label>
              <Input
                id="riferimento_ordine"
                value={formData.riferimento_ordine}
                onChange={(e) => handleChange('riferimento_ordine', e.target.value)}
                placeholder="Es. ORD-2026-0030"
              />
              <p className="text-sm text-muted-foreground">
                Se valorizzato, i movimenti di assemblaggio verranno collegati a questo ordine.
              </p>
            </div>

            {/* Note */}
            <div className="space-y-2">
              <Label htmlFor="note">Note (opzionale)</Label>
              <Textarea
                id="note"
                value={formData.note}
                onChange={(e) => handleChange('note', e.target.value)}
                placeholder="Note produzione..."
                rows={3}
              />
            </div>

            {/* Info Assemblaggio */}
            <div className="rounded-lg bg-blue-50 p-4 text-sm">
              <p className="font-semibold text-blue-900 mb-2">ℹ️ Cosa succede quando crei il lotto:</p>
              <ul className="list-disc list-inside text-blue-800 space-y-1">
                <li>Scarica automaticamente i componenti scatola (fondo, coperchio, fustelle, paglia)</li>
                <li>Scarica automaticamente i prodotti dalla composizione box</li>
                <li>Carica le box assemblate pronte per la vendita</li>
                <li>Crea movimenti magazzino per tracciare l&apos;operazione</li>
              </ul>
            </div>

            {/* Azioni */}
            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
              >
                Annulla
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? 'Assemblaggio...' : 'Crea Lotto e Assembla'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  )
}
